import api from "./api";
import { Avaliation } from "../types";
import Objects from "../constants/Objects";

type AvaliationInput = Avaliation & {
  professional?: { id: string | number } | string | number;
  user?: { id: string | number } | string | number;
  [key: string]: unknown;
};

function preparePayload(body: Avaliation) {
  // Extract root fields known to backend
  const {
    id,
    _id,
    professional_id,
    professional,
    user_id,
    user,
    date,
    registe,
    images,
    observations,
    motivacao,
    sono,
    frequencia_treino,
    created_at,
    updated_at,
    avaliation: existingAvaliation, // Extract and exclude this to prevent nesting
    ...measurements
  } = body as AvaliationInput & { avaliation?: unknown };

  // Determine user and professional IDs
  // Handle both string/number IDs and object representations
  const pId =
    typeof professional === "object" &&
    professional !== null &&
    "id" in professional
      ? (professional as { id: string | number }).id
      : professional || professional_id;
  const uId =
    typeof user === "object" && user !== null && "id" in user
      ? (user as { id: string | number }).id
      : user || user_id;

  // Ensure date is present (default to today if missing)
  // Backend expects 'YYYY-MM-DD'
  const d = date || new Date().toISOString().split("T")[0];

  // Clean measurements: remove any nested 'avaliation' fields to prevent double nesting
  const cleanMeasurements = { ...measurements };
  if ('avaliation' in cleanMeasurements) {
    delete cleanMeasurements.avaliation;
  }

  // Convert additional info fields to integers if they exist
  const convertToInt = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const num = typeof value === "string" ? parseInt(value, 10) : Number(value);
    // Allow 0 as a valid value (user can select 0 stars/dumbbells)
    return Number.isNaN(num) ? null : num;
  };

  return {
    professional: pId,
    user: uId,
    date: d,
    images: images || [],
    observations: observations || "",
    motivacao: convertToInt(motivacao),
    sono: convertToInt(sono),
    frequencia_treino: convertToInt(frequencia_treino),
    avaliation: cleanMeasurements, // All other fields go into the JSON field
  };
}

async function create(body: Avaliation) {
  try {
    const payload = preparePayload(body);
    const { data } = await api.post(`/avaliations/`, payload);
    return data;
  } catch (e) {
    return false;
  }
}

async function update(body: Avaliation) {
  try {
    const payload = preparePayload(body);
    // Use id or _id, preferring id
    const id = body.id || body._id;
    const { data } = await api.put(`/avaliations/${id}/`, payload);
    return data;
  } catch (e) {
    return false;
  }
}

async function listall(
  professional_id: string,
  user_id: string
): Promise<Avaliation[]> {
  try {
    const { data } = await api.get(
      `/avaliations/${professional_id}/${user_id}/`
    );
    // Flatten the response: merge nested 'avaliation' (measurements) into the root
    // and ensure default structure from Objects.avalicao to prevent missing fields
    return data.map((item: Record<string, unknown>) => {
      const { avaliation, ...rest } = item;
      return {
        ...Objects.avalicao, // Start with default structure
        ...((avaliation as Record<string, unknown>) || {}), // Overlay measurements
        ...rest, // Overlay root fields (id, date, motivacao, sono, frequencia_treino, etc.)
      } as Avaliation;
    });
  } catch (e) {
    return [];
  }
}

async function remove(id: string) {
  try {
    const { data } = await api.delete(`/avaliations/${id}/`);
    return data;
  } catch (e) {
    return false;
  }
}

async function analyzeDescription(description: string) {
  try {
    const { data } = await api.post(`/avaliations/analyze-description/`, {
      description,
    });
    return data;
  } catch (e: unknown) {
    console.error("Erro ao analisar descrição:", e);
    const error = e as { response?: { data?: { error_code?: string; details?: string } } };
    return {
      error: error.response?.data?.error_code || "unknown_error",
      message: error.response?.data?.details || "Erro ao processar descrição",
    };
  }
}

async function analyzeAudio(audioBlob: Blob) {
  try {
    const formData = new FormData();
    formData.append("audio", audioBlob, "recording.webm");

    const { data } = await api.post(`/avaliations/analyze-audio/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (e: unknown) {
    console.error("Erro ao analisar áudio:", e);
    const error = e as { response?: { data?: { error_code?: string; details?: string } } };
    return {
      error: error.response?.data?.error_code || "unknown_error",
      message: error.response?.data?.details || "Erro ao processar áudio",
    };
  }
}

async function analyzeImage(imageFile: File) {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    const { data } = await api.post(`/avaliations/analyze-image/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (e: unknown) {
    console.error("Erro ao analisar imagem:", e);
    const error = e as { response?: { data?: { error_code?: string; details?: string } } };
    return {
      error: error.response?.data?.error_code || "unknown_error",
      message: error.response?.data?.details || "Erro ao processar imagem",
    };
  }
}

const avaliations = {
  create,
  update,
  listall,
  remove,
  analyzeDescription,
  analyzeAudio,
  analyzeImage,
};

export default avaliations;
